import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import {
  createMcpToken,
  fetchMcpAudit,
  fetchMcpCoverage,
  fetchMcpInfo,
  fetchMcpTokens,
  revokeAllMcpTokens,
  revokeMcpToken,
} from '../api';
import type { McpCreateTokenPayload } from '../types';

/** Корень ключей react-query для раздела MCP. */
const MCP_KEY = 'mcp-access';

/** Сведения о подключении MCP. */
export function useMcpInfo() {
  return useQuery({ queryKey: [MCP_KEY, 'info'], queryFn: fetchMcpInfo });
}

/** Токены: свои (all=false) или всех админов (all=true). */
export function useMcpTokens(all: boolean, includeRevoked: boolean, enabled = true) {
  return useQuery({
    queryKey: [MCP_KEY, 'tokens', all, includeRevoked],
    queryFn: () => fetchMcpTokens(all, includeRevoked),
    enabled,
  });
}

/** Страница журнала вызовов. */
export function useMcpAudit(all: boolean, page: number, limit: number) {
  return useQuery({
    queryKey: [MCP_KEY, 'audit', all, page, limit],
    queryFn: () => fetchMcpAudit(all, page, limit),
    placeholderData: (previous) => previous,
  });
}

/** Отчёт покрытия API (только mcp.manage). */
export function useMcpCoverage(enabled: boolean) {
  return useQuery({
    queryKey: [MCP_KEY, 'coverage'],
    queryFn: fetchMcpCoverage,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidateTokens() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [MCP_KEY, 'tokens'] });
}

/** Создание персонального токена. */
export function useCreateMcpToken() {
  const invalidate = useInvalidateTokens();
  return useMutation({
    mutationFn: (payload: McpCreateTokenPayload) => createMcpToken(payload),
    onSuccess: () => {
      void invalidate();
    },
    onError: (error: Error) => notify.error(error.message),
  });
}

/** Отзыв одного токена. */
export function useRevokeMcpToken() {
  const invalidate = useInvalidateTokens();
  return useMutation({
    mutationFn: (id: number) => revokeMcpToken(id),
    onSuccess: () => {
      notify.success('Токен отозван');
      void invalidate();
    },
    onError: (error: Error) => notify.error(error.message),
  });
}

/** Отзыв всех своих токенов. */
export function useRevokeAllMcpTokens() {
  const invalidate = useInvalidateTokens();
  return useMutation({
    mutationFn: () => revokeAllMcpTokens(),
    onSuccess: (revoked) => {
      notify.success(revoked > 0 ? `Отозвано токенов: ${revoked}` : 'Активных токенов не было');
      void invalidate();
    },
    onError: (error: Error) => notify.error(error.message),
  });
}
